import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api";
import type { AllowlistEntry, HandlePreview } from "../types";

function fmtDate(v: string | null | undefined): string {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString();
}


/**
 * Matches an entry against the search box.
 *
 * Phone numbers are stored as bare digits (`16502530000`) but get copied around
 * as `+1 (650) 253-0000`, so a digit-only comparison runs alongside the plain
 * substring one — otherwise pasting a number from anywhere else finds nothing.
 */
function matches(e: AllowlistEntry, q: string): boolean {
  const hay = `${e.id} ${e.display} ${e.note ?? ""} ${e.added_by ?? ""}`.toLowerCase();
  if (hay.includes(q.toLowerCase())) return true;
  const digits = q.replace(/\D/g, "");
  return digits.length > 0 && e.id.includes(digits);
}

/**
 * `imessage_allowlist` — the collection that decides who Bean answers. Entries
 * arrive from waitlist promotion, from the add form below, or from a hand edit
 * in the Firestore console; the last is distinguishable by having no `added_by`.
 *
 * Rows arrive newest-first, sorted by the API on `created_at`. An allowlist
 * document written without that field grants access but does not appear here —
 * see `allowlist.list_entries`.
 *
 * As in `PromoteDialog`, the handle typed into the add form is resolved by the
 * SERVER and shown before anything is written. No phone parsing lives here: a
 * second implementation in TypeScript would eventually disagree with the one
 * inbound messages are matched against, and the failure is silent.
 */
export function Allowlist() {
  const [rows, setRows] = useState<AllowlistEntry[]>([]);
  const [cursor, setCursor] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [raw, setRaw] = useState("");
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState<HandlePreview | null>(null);
  const [checking, setChecking] = useState(false);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState("");

  const load = useCallback(async (next = "") => {
    setLoading(true);
    setError("");
    try {
      const page = await api.listAllowlist(next);
      setRows((prev) => (next ? [...prev, ...page.items] : page.items));
      setCursor(page.next_cursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Re-resolve as the operator types, debounced. An empty box resolves to
  // nothing rather than asking the server to reject "".
  useEffect(() => {
    const typed = raw.trim();
    if (!typed) {
      setPreview(null);
      setChecking(false);
      return;
    }
    let cancelled = false;
    setChecking(true);
    const t = setTimeout(() => {
      api
        .previewAllowlistHandle(typed)
        .then((p) => !cancelled && setPreview(p))
        .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
        .finally(() => !cancelled && setChecking(false));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [raw]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!preview?.valid || adding) return;
    setAdding(true);
    setError("");
    setAdded("");
    try {
      // Send the text, not the resolved id: the server resolves once more, so a
      // stale preview can never be what gets written.
      const res = await api.addAllowlist(raw.trim(), note.trim());
      setAdded(
        res.already_allowlisted
          ? `${res.display} was already on the list — its note was refreshed.`
          : `${res.display} can now message Bean.`,
      );
      setRaw("");
      setNote("");
      setPreview(null);
      setCursor("");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setAdding(false);
    }
  }

  // The API already returns rows newest-first, so this only filters.
  const visible = useMemo(() => {
    const q = query.trim();
    return q ? rows.filter((r) => matches(r, q)) : rows;
  }, [rows, query]);

  return (
    <section>
      <header className="section-head">
        <h2>Allowlist</h2>
        <button
          className="ghost"
          onClick={() => {
            setCursor("");
            void load();
          }}
          disabled={loading}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </header>

      <p className="muted small">
        Handles Bean will reply to. Inbound iMessages from anyone not on this list
        are silently ignored.
      </p>

      <form className="add-admin" onSubmit={add}>
        <input
          placeholder="Phone number or email to add"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          spellCheck={false}
          autoComplete="off"
          aria-label="Phone number or email to add"
        />
        <input
          placeholder="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={200}
          autoComplete="off"
          aria-label="Note"
        />
        <button className="primary" disabled={!preview?.valid || adding}>
          {adding ? "Adding…" : "Add to allowlist"}
        </button>
      </form>

      {/* What will actually be written. A number typed without a country code
          picks one up here, and seeing that before the write is the whole point
          of resolving server-side. */}
      <div className="resolved" aria-live="polite">
        {checking && <span className="muted">Checking…</span>}
        {!checking && preview?.valid && (
          <>
            <span className="ok">Will be written as</span>{" "}
            <code>{preview.handle}</code>
            {preview.already_allowlisted && (
              <span className="muted"> · already in the allowlist</span>
            )}
          </>
        )}
        {!checking && preview && !preview.valid && (
          <span className="error">{preview.reason}</span>
        )}
        {!checking && !preview && added && <span className="ok">{added}</span>}
      </div>

      <div className="add-admin">
        <input
          type="search"
          placeholder="Search by number, email, or who added it"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
        />
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {!loading && visible.length === 0 && (
        <p className="muted empty">
          {query ? "No entry matches that." : "Nobody is allowlisted yet."}
        </p>
      )}

      {visible.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Handle</th>
                <th>Key</th>
                <th>Added by</th>
                <th>Added ▾</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((e) => (
                <tr key={e.id}>
                  <td>{e.display}</td>
                  {/* The doc id is what inbound traffic is matched against, so
                      it is worth showing even though it duplicates the column
                      to its left — a mismatch here is the failure mode. */}
                  <td>
                    <code className="muted">{e.id}</code>
                  </td>
                  <td>
                    {e.added_by || (
                      <span className="badge" title="No added_by field on the document">
                        console
                      </span>
                    )}
                  </td>
                  <td className="nowrap muted">
                    {fmtDate(e.created_at ?? e.added_at) || "—"}
                  </td>
                  <td className="muted small">{e.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="muted small">
        {query
          ? `${visible.length} of ${rows.length} loaded ${rows.length === 1 ? "entry" : "entries"}`
          : `${rows.length} ${rows.length === 1 ? "entry" : "entries"}${cursor ? " so far" : ""}`}
        {cursor && (
          <>
            {" · "}
            {/* Search only filters what has been loaded, so say so while there
                is more to fetch. */}
            <button
              className="ghost small"
              disabled={loading}
              onClick={() => void load(cursor)}
            >
              Load more
            </button>
          </>
        )}
      </p>

      <p className="muted small">
        Entries come from promoting a waitlist signup or from the box above.
        Removing access is not possible from here — do it in the Firestore
        console.
      </p>
    </section>
  );
}
